import { useMemo, useState } from 'react';
import { Icon } from './Icon';
import { today, toISODate, fmtShort } from '../../lib/dates';

/** Same density palette as `/newsletters` and `/social` so the picker
 *  reads as a small version of the same chart. */
const heatmapBg = (count: number) => {
  if (count === 0) return 'rgba(232, 163, 62, 0.18)';
  if (count === 1) return 'rgba(43, 138, 90, 0.12)';
  if (count === 2) return 'rgba(43, 138, 90, 0.25)';
  return 'rgba(43, 138, 90, 0.45)';
};

const buildMonthGrid = (year: number, month: number): Date[] => {
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - dow);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export interface DayDensity {
  count: number;
  /** Names to surface on hover (campaign names, asset titles, …). Optional. */
  labels?: string[];
}

interface Props {
  /** ISO yyyy-mm-dd currently picked, or '' / null. */
  value: string | null;
  onPick: (iso: string) => void;
  /** Precomputed density per ISO day. Days missing from the map render as empty. */
  dayItems: Map<string, DayDensity>;
  /** Trigger button label and tooltip (e.g. "See planning", "See campaigns"). */
  triggerLabel: string;
  triggerTooltip?: string;
  /** Tooltip prefix for "N planned" / "N running" hint on each day. */
  countNoun?: string;
}

/** Inline-expandable monthly heatmap picker. Closed by default to keep the
 *  surrounding form quiet; click the trigger to open. Click a day → picks
 *  it and the picker collapses. */
export const HeatmapPicker = ({
  value,
  onPick,
  dayItems,
  triggerLabel,
  triggerTooltip,
  countNoun = 'planned',
}: Props) => {
  const [open, setOpen] = useState(false);
  const initialMonth = useMemo(() => {
    const seed = value ? new Date(value) : today();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  }, [value]);
  const [cursor, setCursor] = useState<Date>(initialMonth);

  const grid = useMemo(
    () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );

  const weekCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (let i = 0; i < grid.length; i += 7) {
      let n = 0;
      for (let j = 0; j < 7; j++) {
        n += dayItems.get(toISODate(grid[i + j]))?.count ?? 0;
      }
      map.set(i / 7, n);
    }
    return map;
  }, [grid, dayItems]);

  const monthLabel = cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const todayIso = toISODate(today());

  const pick = (d: Date) => {
    onPick(toISODate(d));
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: '5px 10px',
          cursor: 'pointer',
          fontSize: 11.5,
          color: 'var(--ink-3)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        aria-expanded={open}
        title={triggerTooltip ?? (open ? 'Hide' : 'Show')}
      >
        <Icon name="calendar" size={11} />
        {open ? 'Hide' : triggerLabel}
      </button>

      {open && (
        <div
          style={{
            marginTop: 8,
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: 10,
            background: 'var(--bg-elevated)',
            maxWidth: 360,
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
              aria-label="Previous month"
            >
              <Icon name="chevronLeft" size={12} />
            </button>
            <div style={{ fontSize: 12, fontWeight: 600, flex: 1, textAlign: 'center' }}>
              {monthLabel}
            </div>
            <button
              type="button"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
              aria-label="Next month"
            >
              <Icon name="chevronRight" size={12} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {DOW.map((d, i) => (
              <div key={i} style={{ fontSize: 9.5, color: 'var(--ink-4)', textAlign: 'center', fontWeight: 600, letterSpacing: '0.05em' }}>
                {d}
              </div>
            ))}
            {grid.map((d, i) => {
              const iso = toISODate(d);
              const outside = d.getMonth() !== cursor.getMonth();
              const isToday = iso === todayIso;
              const isSelected = iso === value;
              const day = dayItems.get(iso);
              const count = day?.count ?? 0;
              const weekIdx = Math.floor(i / 7);
              const weekCount = weekCounts.get(weekIdx) ?? 0;
              const isGapWeek = weekCount === 0 && !outside;
              const titleParts = [fmtShort(d)];
              if (count > 0) {
                titleParts.push(`${count} ${countNoun}`);
                if (day?.labels && day.labels.length > 0) {
                  titleParts.push(day.labels.slice(0, 5).join(', '));
                }
              } else if (isGapWeek) {
                titleParts.push('Gap week');
              }
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => pick(d)}
                  title={titleParts.join(' · ')}
                  style={{
                    aspectRatio: '1 / 1',
                    minHeight: 30,
                    border: isSelected
                      ? '2px solid var(--ink)'
                      : isToday
                      ? '1px solid var(--ink-3)'
                      : '1px solid transparent',
                    borderRadius: 'var(--r-sm)',
                    background: outside ? 'transparent' : heatmapBg(count),
                    color: outside ? 'var(--ink-5)' : 'var(--ink)',
                    fontSize: 11,
                    fontWeight: isToday || isSelected ? 600 : 500,
                    cursor: 'pointer',
                    padding: 0,
                    position: 'relative',
                  }}
                >
                  {d.getDate()}
                  {count > 0 && !outside && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: 2,
                        right: 3,
                        fontSize: 8.5,
                        color: 'var(--ink-3)',
                        fontWeight: 600,
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 8, display: 'flex', gap: 10, fontSize: 10.5, color: 'var(--ink-4)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: heatmapBg(0), display: 'inline-block' }} />
              Empty
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: heatmapBg(2), display: 'inline-block' }} />
              Some
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: heatmapBg(4), display: 'inline-block' }} />
              Busy
            </span>
            <span style={{ marginLeft: 'auto' }}>Hover for details · click to pick.</span>
          </div>
        </div>
      )}
    </>
  );
};

export default HeatmapPicker;
